/**
 * Estado global de sesión y preferencias.
 *
 * Las preferencias funcionan también sin cuenta: se guardan en `localStorage`
 * y, al iniciar sesión, mandan las del servidor. Así el tablero se ve igual
 * en cualquier dispositivo, pero un visitante anónimo también puede
 * personalizarlo.
 */

import { create } from 'zustand';
import { DEFAULT_PREFS, type PrivateUser, type UserPrefs } from '@shared/types';
import { api, ApiRequestError } from '@/lib/api';
import { socket } from '@/lib/socket';
import { forgetPushForSession } from '@/lib/push';

const PREFS_KEY = 'ua_chess_prefs';

function readLocalPrefs(): UserPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    // Se fusiona con los valores por defecto para que una versión nueva de la
    // app no se quede sin las preferencias que aún no existían.
    return { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<UserPrefs>) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function writeLocalPrefs(prefs: UserPrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Almacenamiento lleno o bloqueado: la app sigue funcionando en memoria.
  }
}

interface SessionState {
  user: PrivateUser | null;
  prefs: UserPrefs;
  /** `true` hasta que sabemos si hay sesión: evita parpadeos al cargar. */
  loading: boolean;
  ready: boolean;

  bootstrap: () => Promise<void>;
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  register: (data: {
    username: string;
    email: string;
    password: string;
    country?: string | null;
    birthDate?: string | null;
    gender?: 'female' | 'male' | 'other' | null;
  }) => Promise<void>;
  /** Crea la cuenta de quien volvió de Google sin tener una. */
  completeGoogleSignup: (data: { username: string; country?: string | null }) => Promise<void>;
  logout: () => Promise<void>;
  setPrefs: (patch: Partial<UserPrefs>) => void;
  setUser: (user: PrivateUser | null) => void;
}

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  prefs: readLocalPrefs(),
  loading: true,
  ready: false,

  /** Se llama una vez al arrancar la aplicación. */
  async bootstrap() {
    try {
      const user = await api.get<PrivateUser>('/auth/me');
      set({ user, prefs: { ...DEFAULT_PREFS, ...user.prefs } });
      writeLocalPrefs({ ...DEFAULT_PREFS, ...user.prefs });
    } catch (error) {
      if (!(error instanceof ApiRequestError) || !error.isAuth) {
        console.warn('No se pudo comprobar la sesión:', error);
      }
      set({ user: null });
    } finally {
      set({ loading: false, ready: true });
      socket.connect();
    }
  },

  async login(usernameOrEmail, password) {
    const user = await api.post<PrivateUser>('/auth/login', { usernameOrEmail, password });
    set({ user, prefs: { ...DEFAULT_PREFS, ...user.prefs } });
    writeLocalPrefs({ ...DEFAULT_PREFS, ...user.prefs });
    // Se reabre el socket para que el servidor asocie la conexión al usuario.
    socket.disconnect();
    socket.connect();
  },

  async register(data) {
    // La cuenta nueva hereda las preferencias con las que el visitante ya
    // estaba jugando.
    const user = await api.post<PrivateUser>('/auth/register', {
      ...data,
      prefs: get().prefs,
    });
    set({ user });
    socket.disconnect();
    socket.connect();
  },

  async completeGoogleSignup(data) {
    const user = await api.post<PrivateUser>('/auth/google/complete', {
      ...data,
      prefs: get().prefs,
    });
    set({ user });
    socket.disconnect();
    socket.connect();
  },

  async logout() {
    try {
      // Antes de cerrar la sesión, que aún hace falta para darse de baja.
      await forgetPushForSession();
      await api.post('/auth/logout');
    } finally {
      set({ user: null });
      socket.disconnect();
      socket.connect();
    }
  },

  setPrefs(patch) {
    const prefs = { ...get().prefs, ...patch };
    set({ prefs });
    writeLocalPrefs(prefs);

    if (get().user) {
      // Se guarda sin bloquear la interfaz: el cambio ya se ve al instante.
      void api.patch('/auth/prefs', patch).catch(() => {
        console.warn('No se pudieron guardar las preferencias en el servidor.');
      });
    }
  },

  setUser(user) {
    set({ user });
  },
}));

/** Atajo para componentes que solo necesitan saber quién está conectado. */
export const useUser = () => useSession((s) => s.user);
export const usePrefs = () => useSession((s) => s.prefs);
